import { Link, useNavigate } from "react-router-dom";
import { Drawer } from "rsuite";
import "rsuite/dist/rsuite.min.css";
import { useSelector, useDispatch } from "react-redux";
import { sidebarAction } from "../../store/reducers/sidebarSlice";
import logo from "../../assets/img/logo.png";
import { resourcesLinks } from "../../constants/resourcesLinks";
import { useCategoriesInfiniteQuery } from "../../hooks/queries/useCategoriesInfiniteQuery";

const SideBar = () => {
  const open = useSelector((state) => state.sidebar.value);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.user);

  // Paginated and scoped to the buyer's assigned categories server-side.
  const {
    categories,
    isLoading: loading,
    error,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useCategoriesInfiniteQuery({
    assigned: true,
    enabled: Boolean(open && user),
  });

  return (
    <Drawer
      size={"xs"}
      placement="left"
      open={open}
      onClose={() => dispatch(sidebarAction(false))}
    >
      <div className="drawer-mask"></div>
      <div className="flex flex-col w-full h-full bg-white cursor-pointer scrollbar-hide">
        <div className="w-full flex justify-between items-center h-16 px-6 py-4 bg-brand-600 text-white border-b border-gray-100">
          <h2 className="font-semibold text-lg m-0 text-heading flex items-center">
            <Link className="mr-10" to="/">
              <span
                style={{
                  boxSizing: "border-box",
                  display: "inline-block",
                  overflow: "hidden",
                  width: "initial",
                  height: "initial",
                  background: "none",
                  opacity: "1",
                  border: "0px",
                  margin: "0px",
                  padding: "0px",
                  position: "relative",
                  maxWidth: "100%",
                }}
              >
                <img className="h-9 w-24" src={logo} alt="Wholesale Dai" />
              </span>
            </Link>
          </h2>
          <button
            onClick={() => dispatch(sidebarAction(false))}
            className="flex text-xl items-center justify-center w-8 h-8 rounded-full bg-gray-50 text-red-500 p-2 focus:outline-none transition-opacity hover:text-red-600"
          >
            <svg
              className="group-hover:text-red-400"
              stroke="currentColor"
              fill="currentColor"
              strokeWidth="0"
              viewBox="0 0 512 512"
              height="1em"
              width="1em"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M289.94 256l95-95A24 24 0 00351 127l-95 95-95-95a24 24 0 00-34 34l95 95-95 95a24 24 0 1034 34l95-95 95 95a24 24 0 0034-34z"></path>
            </svg>
          </button>
        </div>
        <div className="overflow-y-scroll scrollbar-hide w-full max-h-full">
          <h2 className="font-semibold text-lg m-0 text-heading flex align-center border-b px-8 py-3">
            All Categories
          </h2>
          <div className="relative grid gap-2 p-6">
            {loading ? (
              <div className="text-center py-4">
                <svg
                  className="animate-spin h-5 w-5 text-brand-600 mx-auto"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                <p className="text-sm text-gray-500 mt-2">Loading categories...</p>
              </div>
            ) : error ? (
              <div className="text-center py-4 text-red-500 text-sm">
                Failed to load categories
              </div>
            ) : categories.length > 0 ? (
              categories.map((category, index) => {
                return (
                  <div key={index}>
                    <Link
                      onClick={() => {
                        navigate(`/search?Category=${category.slug}`);
                        dispatch(sidebarAction(false));
                      }}
                      to={`/search?Category=${category.slug}`}
                      className="flex items-center rounded-md w-full !no-underline px-3 py-2 bg-gray-200 text-black hover:bg-brand-600 hover:text-white cursor-pointer transition-colors"
                    >
                      <span
                        style={{
                          boxSizing: "border-box",
                          display: "inline-block",
                          overflow: "hidden",
                          width: "initial",
                          height: "initial",
                          background: "none",
                          opacity: "1",
                          border: "0px",
                          margin: "0px",
                          padding: "0px",
                          position: "relative",
                          maxWidth: "100%",
                        }}
                      >
                        <img
                          src={category.banner}
                          alt={category.name}
                          className="h-4 w-4"
                        />
                      </span>
                      <div className="inline-flex items-center justify-between ml-3 text-sm font-medium w-full">
                        {category.name}
                      </div>
                    </Link>
                  </div>
                );
              })
            ) : null}
            {hasNextPage && (
              <button
                type="button"
                onClick={() => fetchNextPage()}
                disabled={isFetchingNextPage}
                className="w-full px-3 py-2 text-sm text-brand-600 rounded-md border border-dashed border-brand-300 hover:bg-brand-50 disabled:opacity-60"
              >
                {isFetchingNextPage ? "Loading…" : "Show more categories"}
              </button>
            )}
          </div>

          <div className="relative grid gap-2 mt-5">
            <h3 className="font-semibold text-lg m-0 text-heading flex align-center border-b px-8 py-3">
              Pages
            </h3>
            <div className="relative grid gap-1 p-6">
              {resourcesLinks.map((item, index) => (
                <Link
                  key={index}
                  to={item.href}
                  onClick={() => dispatch(sidebarAction(false))}
                  className="p-2 flex !text-black items-center rounded-md !no-underline hover:!bg-gray-50 w-full hover:!no-underline hover:!text-brand-600"
                >
                  <item.icon
                    className="flex-shrink-0 h-4 w-4"
                    aria-hidden="true"
                  />
                  <p className="inline-flex items-center justify-between ml-2 text-sm font-medium w-full hover:text-brand-600">
                    {item.name}
                  </p>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </Drawer>
  );
};

export default SideBar;
